import { supabase } from '../utils/supabase';
import { subscriptionApi, userSettingsApi } from '../utils/api';

// Configuration par défaut (fallback en cas d'erreur API)
export const PLANS = {
  DECOUVERTE: 'decouverte',
  STARTER: 'starter',
  ESSENTIEL: 'essentiel',
  CROISSANCE: 'croissance',
  EXCELLENCE: 'excellence'
};

/**
 * Construit une entrée d'offre du fallback à partir des quotas au format
 * serveur (snake_case), pour ne pas dupliquer chaque valeur en camelCase.
 * Miroir de `server/config/plans.config.js` : ne sert que si l'API est injoignable.
 */
const buildFallbackPlan = ({ name, price, limits, features, description }) => ({
  name,
  price,
  currency: 'XOF',
  description,
  limits,
  maxMessagesPerMonth: limits.max_messages_per_month,
  maxShops: limits.max_shops,
  maxProducts: limits.max_products,
  maxDeliveryPersons: limits.max_delivery_persons,
  maxOrdersPerMonth: limits.max_orders_per_month,
  maxZones: limits.max_zones,
  maxUsers: limits.max_users,
  maxMessageTemplates: limits.max_message_templates,
  aiPointsPerMonth: limits.ai_points_per_month,
  features
});

// Configuration par défaut des plans (fallback)
const DEFAULT_PLAN_FEATURES = {
  [PLANS.DECOUVERTE]: buildFallbackPlan({
    name: 'Découverte',
    price: 0,
    description: 'Essai gratuit de 14 jours pour découvrir la plateforme',
    limits: { max_messages_per_month: 20, max_products: 10, max_delivery_persons: 1, max_orders_per_month: 15, max_zones: 1, max_users: 1, max_message_templates: 2, ai_points_per_month: 0, max_shops: 1 },
    features: ['basic_whatsapp', 'basic_orders']
  }),
  [PLANS.STARTER]: buildFallbackPlan({
    name: 'Starter',
    price: 5000,
    description: 'Pour démarrer avec une connexion WhatsApp permanente',
    limits: { max_messages_per_month: 100, max_products: 50, max_delivery_persons: 2, max_orders_per_month: 80, max_zones: 2, max_users: 1, max_message_templates: 5, ai_points_per_month: 150, max_shops: 1 },
    features: ['basic_whatsapp', 'basic_orders', 'whatsapp_automation', 'orders_management', 'delivery_tracking']
  }),
  [PLANS.ESSENTIEL]: buildFallbackPlan({
    name: 'Essentiel',
    price: 15000,
    description: 'Idéal pour les petites entreprises qui structurent leurs ventes',
    limits: { max_messages_per_month: 300, max_products: 200, max_delivery_persons: 5, max_orders_per_month: 250, max_zones: 5, max_users: 3, max_message_templates: 20, ai_points_per_month: 500, max_shops: 1 },
    features: ['basic_whatsapp', 'basic_orders', 'whatsapp_automation', 'orders_management', 'basic_analytics', 'delivery_tracking']
  }),
  [PLANS.CROISSANCE]: buildFallbackPlan({
    name: 'Croissance',
    price: 30000,
    description: 'Pour les entreprises en expansion',
    limits: { max_messages_per_month: 1000, max_products: 1000, max_delivery_persons: 15, max_orders_per_month: 800, max_zones: 15, max_users: 6, max_message_templates: 50, ai_points_per_month: 1200, max_shops: 2 },
    features: ['basic_whatsapp', 'basic_orders', 'whatsapp_automation', 'orders_management', 'basic_analytics', 'delivery_tracking', 'advanced_automation', 'pos_integration', 'cashier_management', 'advanced_analytics', 'marketing_campaigns', 'loyalty_points', 'inventory_sync']
  }),
  [PLANS.EXCELLENCE]: buildFallbackPlan({
    name: 'Excellence',
    price: 75000,
    description: 'Solution complète pour les grandes entreprises',
    limits: { max_messages_per_month: -1, max_products: -1, max_delivery_persons: -1, max_orders_per_month: -1, max_zones: -1, max_users: -1, max_message_templates: -1, ai_points_per_month: 3000, max_shops: -1 },
    features: ['basic_whatsapp', 'basic_orders', 'whatsapp_automation', 'orders_management', 'basic_analytics', 'delivery_tracking', 'advanced_automation', 'pos_integration', 'cashier_management', 'advanced_analytics', 'marketing_campaigns', 'loyalty_points', 'inventory_sync', 'unlimited_all', 'custom_integrations', 'api_access', 'white_label', 'dedicated_support', 'custom_analytics']
  })
};

// Cache pour les plans récupérés via API
let cachedPlansConfig = null;
let cacheTimestamp = null;
let pendingRequest = null; // Pour éviter les requêtes parallèles
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache pour les user settings
let cachedUserSettings = null;
let userSettingsCacheTimestamp = null;
let pendingUserSettingsRequest = null;
const USER_SETTINGS_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

// Utilisation de l'instance Supabase partagée (suppression de l'instance dupliquée)

class PlanService {
  constructor() {
    this.userSettings = null;
    this.currentPlan = null;
    this.planFeatures = null;
    this.planOrder = ['decouverte', 'starter', 'essentiel', 'croissance', 'excellence'];
  }

  /**
   * Récupère la configuration des plans depuis l'API avec cache
   */
  async getPlansConfig() {
    // Vérifier le cache
    if (cachedPlansConfig && cacheTimestamp && 
        (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      return cachedPlansConfig;
    }

    // Si une requête est déjà en cours, attendre son résultat
    if (pendingRequest) {
      return await pendingRequest;
    }

    // Démarrer une nouvelle requête
    pendingRequest = this._fetchPlansFromAPI();
    
    try {
      const result = await pendingRequest;
      return result;
    } finally {
      // Nettoyer la requête en cours
      pendingRequest = null;
    }
  }

  /**
   * Méthode privée pour récupérer les plans depuis l'API
   */
  async _fetchPlansFromAPI() {
    try {
      const response = await subscriptionApi.getAvailablePlans();
      if (response?.data?.success) {
        // Convertir les données API vers le format attendu par le client
        const apiPlans = response.data.data;
        const convertedPlans = {};

        Object.keys(apiPlans).forEach(planKey => {
          const apiPlan = apiPlans[planKey];
          convertedPlans[planKey] = {
            name: apiPlan.name,
            // Conserver le format serveur pour les écrans qui affichent tous les quotas.
            limits: apiPlan.limits,
            maxMessagesPerMonth: apiPlan.limits.max_messages_per_month,
            maxShops: apiPlan.limits.max_shops,
            maxProducts: apiPlan.limits.max_products,
            maxDeliveryPersons: apiPlan.limits.max_delivery_persons,
            maxOrdersPerMonth: apiPlan.limits.max_orders_per_month,
            maxZones: apiPlan.limits.max_zones,
            maxUsers: apiPlan.limits.max_users,
            maxMessageTemplates: apiPlan.limits.max_message_templates,
            aiPointsPerMonth: apiPlan.limits.ai_points_per_month,
            features: apiPlan.features,
            price: apiPlan.price,
            currency: apiPlan.currency,
            description: apiPlan.description,
            recommended_for: apiPlan.recommended_for,
            icon: apiPlan.icon,
            color: apiPlan.color
          };
        });
        
        // Mettre à jour le cache
        cachedPlansConfig = convertedPlans;
        cacheTimestamp = Date.now();
        
        // Mettre à jour l'ordre des plans si fourni
        if (response.data.order) {
          this.planOrder = response.data.order;
        }

        return convertedPlans;
      }
    } catch (error) {
      console.warn('❌ Erreur lors du chargement des plans depuis l\'API, utilisation du fallback:', error);
    }

    // Fallback vers la configuration par défaut
    return DEFAULT_PLAN_FEATURES;
  }

  /**
   * Force le rechargement des plans depuis l'API
   */
  async refreshPlansConfig() {
    cachedPlansConfig = null;
    cacheTimestamp = null;
    pendingRequest = null; // Nettoyer aussi la requête en cours
    return await this.getPlansConfig();
  }

  /**
   * Force le rechargement des user settings depuis l'API
   */
  async refreshUserSettings() {
    cachedUserSettings = null;
    userSettingsCacheTimestamp = null;
    pendingUserSettingsRequest = null; // Nettoyer aussi la requête en cours
    return await this.loadUserSettings();
  }

  /**
   * Initialise ou recharge les données utilisateur
   * @param {Object} session - Session utilisateur (optionnel, sera récupérée si non fournie)
   */
  async loadUserSettings(session = null) {
    // Vérifier le cache des user settings
    if (cachedUserSettings && userSettingsCacheTimestamp && 
        (Date.now() - userSettingsCacheTimestamp) < USER_SETTINGS_CACHE_DURATION) {
      console.log('🚀 User settings récupérés depuis le cache');
      this.userSettings = cachedUserSettings;
      this.currentPlan = cachedUserSettings?.billing_plan || 'decouverte';
      this.planFeatures = cachedUserSettings?.plan_features || {};
      
      // Sauvegarder également en sessionStorage pour une persistance inter-onglets
      if (cachedUserSettings?.billing_plan) {
        try {
          sessionStorage.setItem('lastKnownPlan', cachedUserSettings.billing_plan);
        } catch (e) {
          console.warn('Impossible de sauvegarder le plan dans sessionStorage:', e);
        }
      }
      
      return cachedUserSettings;
    }

    // Si une requête est déjà en cours, attendre son résultat
    if (pendingUserSettingsRequest) {
      console.log('⏳ Requête user settings en cours, attente...');
      return await pendingUserSettingsRequest;
    }

    // Démarrer une nouvelle requête
    console.log('🌐 Nouvelle requête API pour les user settings');
    pendingUserSettingsRequest = this._fetchUserSettingsFromAPI(session);
    
    try {
      const result = await pendingUserSettingsRequest;
      return result;
    } finally {
      // Nettoyer la requête en cours
      pendingUserSettingsRequest = null;
    }
  }

  /**
   * Méthode privée pour récupérer les user settings depuis l'API
   */
  async _fetchUserSettingsFromAPI(session = null) {
    try {
      // Vérifier d'abord l'authentification si nécessaire
      if (!session) {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        if (error) throw error;
        session = currentSession;
      }

      if (!session?.user) {
        console.warn('Aucune session utilisateur trouvée, utilisation des valeurs par défaut');
        this.currentPlan = 'decouverte';
        this.planFeatures = {};
        this.userSettings = null;
        return null;
      }

      // Utiliser l'API côté serveur au lieu de Supabase direct
      const response = await userSettingsApi.getCurrent();
      
      if (response?.data?.success) {
        const data = response.data.data;
        
        console.log('✅ User settings récupérés depuis l\'API et mis en cache');
        // Mettre à jour le cache
        cachedUserSettings = data;
        userSettingsCacheTimestamp = Date.now();
        
        // Mettre à jour les propriétés de l'instance
        this.userSettings = data;
        this.currentPlan = data?.billing_plan || 'decouverte';
        this.planFeatures = data?.plan_features || {};

        // Sauvegarder en sessionStorage
        if (data?.billing_plan) {
          try {
            sessionStorage.setItem('lastKnownPlan', data.billing_plan);
          } catch (e) {
            console.warn('Impossible de sauvegarder le plan dans sessionStorage:', e);
          }
        }

        return data;
      } else {
        throw new Error('Réponse API invalide pour les user settings');
      }

    } catch (error) {
      console.warn('❌ Erreur lors du chargement des paramètres utilisateur, utilisation des valeurs par défaut:', error);
      
      // Valeurs par défaut en cas d'erreur
      this.currentPlan = 'decouverte';
      this.planFeatures = {};
      this.userSettings = null;
      
      return null;
    }
  }

  /**
   * Vérifie les limites d'un type donné
   */
  async checkLimit(limitType, currentCount) {
    const plansConfig = await this.getPlansConfig();
    const planConfig = plansConfig[this.currentPlan];
    if (!planConfig) {
      return { allowed: false, current: currentCount, limit: 0 };
    }

    const limitField = `max${limitType.charAt(0).toUpperCase() + limitType.slice(1)}`;
    const limit = planConfig[limitField];

    if (limit === -1) {
      return { allowed: true, current: currentCount, limit: -1, unlimited: true };
    }

    return {
      allowed: currentCount < limit,
      current: currentCount,
      limit: limit,
      unlimited: false
    };
  }

  /**
   * Vérifie si une fonctionnalité est disponible dans le plan actuel
   */
  async hasFeature(feature) {
    const plansConfig = await this.getPlansConfig();
    console.log('plansConfig', plansConfig);
    console.log('this.currentPlan', this.currentPlan);
    const planConfig = plansConfig[this.currentPlan];
    
    if (!planConfig) return false;
    
    return planConfig.features.includes(feature);
  }

  /**
   * Vérifie si l'utilisateur peut accéder à une route donnée selon son plan
   * @param {string} routePath - Chemin de la route à vérifier
   * @param {Object} session - Session utilisateur (optionnel)
   */
  async canAccessRoute(routePath, session = null) {
    // S'assurer que les paramètres utilisateur sont chargés
    if (!this.userSettings) {
      await this.loadUserSettings(session);
    }

    // Routes de base toujours accessibles
    const basicRoutes = [
      '/app/dashboard',
      '/app/orders',
      '/app/orders/',
      '/app/products',
      '/app/settings',
      '/app/profile'
    ];

    // Vérifier si c'est une route de base
    const isBasicRoute = basicRoutes.some(route => 
      routePath === route || routePath.startsWith(route)
    );

    if (isBasicRoute) {
      return true;
    }

    // Mapping des routes vers les fonctionnalités requises
    const routeFeatureMap = {
      '/app/analytics': 'basic_analytics',
      '/app/delivery-persons': 'delivery_tracking',
      '/app/delivery-persons-orders': 'delivery_tracking',
      '/app/cashiers': 'cashier_management',
      '/app/shops': 'pos_integration',
      '/app/templates': 'whatsapp_automation',
      '/app/clients': 'orders_management',
      '/app/notifications': 'whatsapp_automation',
      '/app/unresponsive-customers': 'advanced_automation'
    };

    // Vérifier la fonctionnalité requise pour la route
    for (const [route, feature] of Object.entries(routeFeatureMap)) {
      if (routePath.startsWith(route)) {
        return await this.hasFeature(feature);
      }
    }

    // Par défaut, autoriser l'accès pour les routes non mappées
    return true;
  }

  /**
   * Vérifie si l'utilisateur peut créer un élément
   */
  async canCreate(type, currentCount) {
    return await this.checkLimit(type, currentCount);
  }

  /**
   * Vérifie les limites de messages
   */
  async checkMessageLimit() {
    if (!this.userSettings) {
      await this.loadUserSettings();
    }

    const isTrialPlan = this.userSettings?.is_trial || false;
    
    if (isTrialPlan) {
      const used = this.userSettings?.trial_messages_used || 0;
      const total = this.userSettings?.trial_max_messages || 15;
      
      return {
        allowed: used < total,
        used: used,
        total: total,
        remaining: Math.max(0, total - used),
        unlimited: false
      };
    } else {
      const plansConfig = await this.getPlansConfig();
      const planConfig = plansConfig[this.currentPlan];
      const maxMessages = planConfig?.maxMessagesPerMonth || 0;
      
      if (maxMessages === -1) {
        return { allowed: true, used: 0, total: -1, unlimited: true };
      }

      // TODO: Implémenter le comptage réel des messages pour les plans payants
      return { allowed: true, used: 0, total: maxMessages, unlimited: false };
    }
  }

  /**
   * Obtient le nom d'un plan
   */
  async getPlanName(planKey) {
    const plansConfig = await this.getPlansConfig();
    return plansConfig[planKey]?.name || planKey;
  }

  /**
   * Obtient le nom d'affichage d'un plan (version synchrone pour l'interface)
   * @param {string} planKey - Clé du plan
   * @returns {string} Nom d'affichage du plan
   */
  getPlanDisplayName(planKey) {
    // Utiliser la configuration par défaut pour un affichage immédiat
    if (!planKey) return 'Découverte';
    
    const planNames = {
      [PLANS.DECOUVERTE]: 'Découverte',
      [PLANS.STARTER]: 'Starter',
      [PLANS.ESSENTIEL]: 'Essentiel',
      [PLANS.CROISSANCE]: 'Croissance',
      [PLANS.EXCELLENCE]: 'Excellence'
    };
    
    return planNames[planKey] || planKey.charAt(0).toUpperCase() + planKey.slice(1);
  }

  /**
   * Récupère les informations complètes du plan actuel de l'utilisateur
   * @returns {Promise<Object|null>} Informations du plan actuel
   */
  async getCurrentPlan() {
    try {
      // S'assurer que les paramètres utilisateur sont chargés
      if (!this.userSettings) {
        await this.loadUserSettings();
      }

      // Si pas de paramètres utilisateur, retourner les valeurs par défaut
      if (!this.userSettings) {
        return {
          billing_plan: 'decouverte',
          plan_name: 'Découverte',
          is_trial: false,
          plan_features: {},
          plan_start_date: null,
          plan_end_date: null
        };
      }

      // Récupérer la configuration des plans pour obtenir les détails
      const plansConfig = await this.getPlansConfig();
      const planConfig = plansConfig[this.userSettings.billing_plan];

      return {
        billing_plan: this.userSettings.billing_plan,
        plan_name: planConfig?.name || this.getPlanDisplayName(this.userSettings.billing_plan),
        is_trial: this.userSettings.is_trial || false,
        plan_features: this.userSettings.plan_features || {},
        plan_start_date: this.userSettings.plan_start_date,
        plan_end_date: this.userSettings.plan_end_date,
        trial_messages_used: this.userSettings.trial_messages_used || 0,
        trial_max_messages: this.userSettings.trial_max_messages || 15,
        // Ajouter les limites du plan
        limits: planConfig ? {
          maxMessagesPerMonth: planConfig.maxMessagesPerMonth,
          maxShops: planConfig.maxShops,
          maxProducts: planConfig.maxProducts,
          maxDeliveryPersons: planConfig.maxDeliveryPersons,
          maxOrdersPerMonth: planConfig.maxOrdersPerMonth,
          maxZones: planConfig.maxZones,
          maxUsers: planConfig.maxUsers,
          maxMessageTemplates: planConfig.maxMessageTemplates,
          aiPointsPerMonth: planConfig.aiPointsPerMonth
        } : null,
        // Ajouter les fonctionnalités disponibles
        availableFeatures: planConfig?.features || []
      };
    } catch (error) {
      console.error('Erreur lors de la récupération du plan actuel:', error);
      
      // Retourner des valeurs par défaut en cas d'erreur
      return {
        billing_plan: 'decouverte',
        plan_name: 'Découverte',
        is_trial: false,
        plan_features: {},
        plan_start_date: null,
        plan_end_date: null,
        limits: null,
        availableFeatures: []
      };
    }
  }

  /**
   * Obtient tous les plans avec une fonctionnalité donnée
   */
  async getPlansWithFeature(feature) {
    const plansConfig = await this.getPlansConfig();
    const plans = Object.keys(plansConfig);
    const result = [];
    
    for (const plan of plans) {
      if (plansConfig[plan].features.includes(feature)) {
        result.push({
          planKey: plan,
          planName: plansConfig[plan].name,
          price: plansConfig[plan].price
        });
      }
    }
    
    return result;
  }

  /**
   * Obtient une recommandation de mise à niveau pour une fonctionnalité donnée
   * @param {string} feature - Fonctionnalité ou route nécessitant un upgrade
   * @returns {Object|null} Recommandation de plan ou null
   */
  getUpgradeRecommendation(feature) {
    // Mapping des fonctionnalités/routes vers les plans recommandés
    const featureRecommendations = {
      // Routes
      '/app/analytics': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      '/app/delivery-persons': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      '/app/cashiers': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      '/app/shops': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      '/app/templates': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      '/app/notifications': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      '/app/unresponsive-customers': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      
      // Fonctionnalités
      'basic_analytics': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      'delivery_tracking': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      'cashier_management': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'pos_integration': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'whatsapp_automation': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' },
      'advanced_automation': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'marketing_campaigns': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'loyalty_points': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'inventory_sync': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'unlimited_all': { planKey: PLANS.EXCELLENCE, planName: 'Excellence' },
      'custom_integrations': { planKey: PLANS.EXCELLENCE, planName: 'Excellence' },
      'api_access': { planKey: PLANS.EXCELLENCE, planName: 'Excellence' },
      
      // Limites : la première offre payante suffit à lever les quotas de l'essai
      'messages_limit': { planKey: PLANS.STARTER, planName: 'Starter' },
      'shops_limit': { planKey: PLANS.CROISSANCE, planName: 'Croissance' },
      'products_limit': { planKey: PLANS.STARTER, planName: 'Starter' },
      'delivery_persons_limit': { planKey: PLANS.STARTER, planName: 'Starter' },
      'users_limit': { planKey: PLANS.ESSENTIEL, planName: 'Essentiel' }
    };

    const recommendation = featureRecommendations[feature];

    if (!recommendation) {
      // Recommandation par défaut si la fonctionnalité n'est pas mappée
      return { planKey: PLANS.STARTER, planName: 'Starter' };
    }

    return recommendation;
  }

  /**
   * Recharge les données utilisateur
   */
  async refresh() {
    await this.refreshPlansConfig();
    return await this.refreshUserSettings();
  }
}

// Export de l'instance singleton
export const planService = new PlanService();

// Export de PLAN_FEATURES pour la compatibilité (sera déprécié)
export let PLAN_FEATURES = DEFAULT_PLAN_FEATURES;

// Fonction pour mettre à jour PLAN_FEATURES dynamiquement
export const updatePlanFeatures = async () => {
  try {
    const plansConfig = await planService.getPlansConfig();
    PLAN_FEATURES = plansConfig;
    return plansConfig;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des PLAN_FEATURES:', error);
    return DEFAULT_PLAN_FEATURES;
  }
};
